import type { Side } from '../game/world';

export const PROTOCOL_VERSION = 5 as const;
export const MAX_PLAYERS = 2;

export type ClientMessage =
  | {
      readonly version: typeof PROTOCOL_VERSION;
      readonly type: 'input';
      readonly sequence: number;
      readonly move: { readonly x: number };
    }
  | { readonly version: typeof PROTOCOL_VERSION; readonly type: 'restart' | 'start' };

export interface PaddleSnapshot {
  readonly id: string;
  readonly side: Side;
  readonly x: number;
  readonly score: number;
}

export interface GameSnapshot {
  readonly tick: number;
  readonly hitCount: number;
  readonly started: boolean;
  readonly paddles: readonly PaddleSnapshot[];
  readonly ball: { readonly x: number; readonly z: number; readonly vx: number; readonly vz: number };
  readonly winner: Side | null;
}

export type ServerMessage =
  | { readonly version: typeof PROTOCOL_VERSION; readonly type: 'welcome'; readonly playerId: string; readonly side: Side }
  | ({ readonly version: typeof PROTOCOL_VERSION; readonly type: 'snapshot' } & GameSnapshot)
  | { readonly version: typeof PROTOCOL_VERSION; readonly type: 'reject'; readonly reason: string };

export function isClientMessage(value: unknown): value is ClientMessage {
  if (!isRecord(value) || value.version !== PROTOCOL_VERSION) return false;
  if (value.type === 'restart' || value.type === 'start') return Object.keys(value).length === 2;
  if (value.type !== 'input' || !Number.isSafeInteger(value.sequence) || (value.sequence as number) < 0 || !isRecord(value.move)) return false;
  return isUnitNumber(value.move.x) && Object.keys(value.move).length === 1;
}

export function isServerMessage(value: unknown): value is ServerMessage {
  if (!isRecord(value) || value.version !== PROTOCOL_VERSION || typeof value.type !== 'string') return false;
  if (value.type === 'welcome') return isId(value.playerId) && isSide(value.side);
  if (value.type === 'reject') return typeof value.reason === 'string' && value.reason.length <= 120;
  if (value.type !== 'snapshot' || !Number.isSafeInteger(value.tick) || !Number.isSafeInteger(value.hitCount)
    || (value.hitCount as number) < 0 || typeof value.started !== 'boolean' || !Array.isArray(value.paddles)) return false;
  if (value.paddles.length > MAX_PLAYERS || !value.paddles.every(isPaddleSnapshot)) return false;
  if (!isRecord(value.ball) || !isSideOrNull(value.winner)) return false;
  const ball = value.ball;
  return ['x', 'z', 'vx', 'vz'].every((key) => isPosition(ball[key]));
}

function isPaddleSnapshot(value: unknown): value is PaddleSnapshot {
  return isRecord(value) && isId(value.id) && isSide(value.side) && isPosition(value.x)
    && Number.isSafeInteger(value.score) && (value.score as number) >= 0 && (value.score as number) <= 99;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isId(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 64;
}
function isSide(value: unknown): value is Side { return value === 'north' || value === 'south'; }
function isSideOrNull(value: unknown): value is Side | null { return value === null || isSide(value); }
function isUnitNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= -1 && value <= 1;
}
function isPosition(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= 1_000;
}
